import {apiFetch} from '../api/fetch'
import {createUuid} from '../lib/create-uuid'
import type {CreateCustomerInput, Customer, CustomerListFilters} from './types'

const SEARCH_DELAY_MS = 150

type ApiCustomerListItem = {
    id: string
    name: string
    slug: string
    contact_count: number
}

type ApiCustomerContact = {
    id: string
    name: string
    title: string | null
    description: string | null
    email: string | null
    phone: string
    created_at: string
}

type ApiCustomerDetail = {
    id: string
    name: string
    slug: string
    contacts: ApiCustomerContact[]
}

function delay(ms: number) {
    return new Promise((resolve) => setTimeout(resolve, ms))
}

function fromApiListItem(customer: ApiCustomerListItem): Customer {
    return {
        id: customer.id,
        name: customer.name,
        slug: customer.slug,
        contactName: '',
        phone: '',
    }
}

function fromApiDetail(detail: ApiCustomerDetail): Customer {
    const contact = detail.contacts[0]
    return {
        id: detail.id,
        name: detail.name,
        slug: detail.slug,
        contactName: contact?.name ?? '',
        phone: contact?.phone ?? '',
        email: contact?.email ?? undefined,
    }
}

function toApiCustomerInput(input: CreateCustomerInput) {
    return {
        name: input.name.trim(),
        slug: input.slug?.trim().toUpperCase() || undefined,
        contact_name: input.contactName.trim(),
        phone: input.phone.trim(),
        email: input.email?.trim() || null,
        title: input.title?.trim() || null,
        description: input.description?.trim() || null,
    }
}

function filterCustomers(customers: Customer[], filters: CustomerListFilters = {}): Customer[] {
    const name = filters.name?.trim().toLowerCase()

    return customers
        .filter((customer) => {
            if (name && !customer.name.toLowerCase().includes(name)) {
                return false
            }
            return true
        })
        .sort((a, b) => a.name.localeCompare(b.name))
}

export type CustomerDataLayer = {
    listCustomers: (filters?: CustomerListFilters) => Promise<Customer[]>
    createCustomer: (input: CreateCustomerInput) => Promise<Customer>
    updateCustomer: (id: string, input: CreateCustomerInput) => Promise<Customer>
    getCustomerById: (id: string) => Customer | undefined
    reset: () => void
}

function createCustomerDataLayer(
    initialCustomers: Customer[],
    searchDelayMs = SEARCH_DELAY_MS,
): CustomerDataLayer {
    let customers = [...initialCustomers]

    return {
        async listCustomers(filters = {}) {
            await delay(searchDelayMs)
            return filterCustomers(customers, filters)
        },

        async createCustomer(input: CreateCustomerInput) {
            await delay(searchDelayMs)
            const customer: Customer = {
                id: createUuid(),
                name: input.name.trim(),
                slug: input.slug?.trim().toUpperCase() ?? 'XXXX',
                contactName: input.contactName.trim(),
                phone: input.phone.trim(),
                email: input.email?.trim() || undefined,
            }
            customers = [...customers, customer]
            return customer
        },

        async updateCustomer(id: string, input: CreateCustomerInput) {
            await delay(searchDelayMs)
            const index = customers.findIndex((customer) => customer.id === id)
            if (index === -1) {
                throw new Error(`Customer not found: ${id}`)
            }
            const customer: Customer = {
                id,
                name: input.name.trim(),
                slug: input.slug?.trim().toUpperCase() ?? customers[index]!.slug,
                contactName: input.contactName.trim(),
                phone: input.phone.trim(),
                email: input.email?.trim() || undefined,
            }
            customers = customers.map((item, itemIndex) => (itemIndex === index ? customer : item))
            return customer
        },

        getCustomerById(id: string) {
            return customers.find((customer) => customer.id === id)
        },

        reset() {
            customers = [...initialCustomers]
        },
    }
}

function createServerCustomerDataLayer(): CustomerDataLayer {
    const cache = new Map<string, Customer>()

    function cacheCustomer(customer: Customer) {
        cache.set(customer.id, customer)
    }

    return {
        async listCustomers(filters = {}) {
            const params = new URLSearchParams({query: filters.name?.trim() ?? ''})
            const results = await apiFetch<ApiCustomerListItem[]>(`/customers?${params.toString()}`)
            const customers = results.map(fromApiListItem)
            customers.forEach(cacheCustomer)
            return customers
        },

        async createCustomer(input: CreateCustomerInput) {
            const created = await apiFetch<ApiCustomerDetail>('/customers', {
                method: 'POST',
                body: JSON.stringify(toApiCustomerInput(input)),
            })
            const customer = fromApiDetail(created)
            cacheCustomer(customer)
            return customer
        },

        async updateCustomer(id: string, input: CreateCustomerInput) {
            const updated = await apiFetch<ApiCustomerDetail>(`/customers/${id}`, {
                method: 'PUT',
                body: JSON.stringify({
                    name: input.name.trim(),
                    slug: input.slug?.trim().toUpperCase() || undefined,
                }),
            })
            const customer = fromApiDetail(updated)
            cacheCustomer(customer)
            return customer
        },

        getCustomerById(id: string) {
            return cache.get(id)
        },

        reset() {
            cache.clear()
        },
    }
}

export const customerDataLayer = createServerCustomerDataLayer()

export function createTestCustomerDataLayer(customers: Customer[] = [], searchDelayMs = 0) {
    return createCustomerDataLayer(customers, searchDelayMs)
}
